<template>
  <EquipmentBase
    :section-id="ScenarioDetailSectionKey.SEAL_SENSOR_MLE"
    :class="{ 'compact-mode': !!stack }"
  >
    <!-- Compact Mode Layout -->
    <template v-if="stack">
      <!-- Seals Section -->
      <div class="compact-section">
        <div class="compact-header-row">
          <div class="flex align-items-center">
            <h3 class="compact-title">
              {{ !isEspOnShore ? "Thrust Chamber" : "Seals" }}
            </h3>
            <CalculatorToggle :section-ids="sealCalculatorSectionKey" />
          </div>
        </div>

        <div class="compact-stats-row flex flex-wrap gap-4">
          <ConverterInput
            :label="
              isEspOnShore
                ? 'Operating Thrust Load'
                : 'Thrust Load at Duty Point'
            "
            :model-value="operatingThrustLoad"
            :base-unit="MassUnits.Pounds"
            :model="MassUnits.options()"
            mode="convert"
            class="compact-stat-input"
          />
          <ConverterInput
            :key="`${Math.ceil(Math.random() * 10000)}-shut-in-thrust-load`"
            :label="isEspOnShore ? 'Shut-in Thrust Load' : 'Max Thrust Load'"
            :model-value="shutInThrustLoad"
            :base-unit="MassUnits.Pounds"
            :target-unit="massUnit"
            :model="MassUnits.options()"
            mode="read"
            class="compact-stat-input"
          />
          <ConverterInput
            :key="`${Math.ceil(
              Math.random() * 10000
            )}-selected-seal-thrust-load-rating`"
            label="Selected Thrust Load Rating"
            :model-value="selectedSealThrustLoadRating"
            :base-unit="MassUnits.Pounds"
            :target-unit="massUnit"
            :model="MassUnits.options()"
            mode="read"
            class="compact-stat-input"
          />
        </div>

        <div>
          <SealMotorOilRow :stack="stack" />
        </div>
        <!-- Seals / Thrust Chambers -->
        <div v-if="!isEspOnShore">
          <div v-if="sealsOrder?.length > 0" class="space-y-3">
            <SealThrustChamberRow
              v-for="(sealXrefId, i) in sealsOrder"
              :key="i"
              :index="i"
              :canEdit="!isScenarioReadonly"
              :seal="getSeal(sealXrefId)"
              :hide-add-above="newSealExists || sealsOrder.length >= maxSeals"
              :hide-add-below="newSealExists || sealsOrder.length >= maxSeals"
              :hide-remove="false"
              @add-above="addSeal(i)"
              @add-below="addSeal(i + 1)"
              @remove="removeSeal($event, i)"
              @update:seals="updateSeal($event, i)"
            />
          </div>
          <div v-else class="mb-3">
            <SealThrustChamberRow
              :seal="generateSealXref()"
              :hide-add-above="true"
              :hide-add-below="true"
              :hide-remove="false"
              @update:seals="addSeal(0, $event)"
            />
          </div>
        </div>
        <div v-else>
          <div v-if="sealsOrder?.length > 0" class="space-y-3">
            <SealRow
              v-for="(sealXrefId, i) in sealsOrder"
              :key="i"
              :index="i"
              :canEdit="!isScenarioReadonly"
              :seal="getSeal(sealXrefId)"
              :stack="stack"
              :hide-add-above="newSealExists"
              :hide-add-below="newSealExists"
              :hide-remove="false"
              @add-above="addSeal(i)"
              @add-below="addSeal(i + 1)"
              @remove="removeSeal($event, i)"
              @update:seals="updateSeal($event, i)"
            />
          </div>
          <div v-else class="mb-3">
            <SealRow
              :seal="generateSealXref()"
              :stack="stack"
              :hide-add-above="true"
              :hide-add-below="true"
              :hide-remove="false"
              @update:seals="addSeal(0, $event)"
            />
          </div>
        </div>
      </div>

      <!-- Sensor Section -->
      <section v-if="isEspOnShore" class="compact-section">
        <div class="compact-header-row">
          <h3 class="compact-title">Sensor</h3>
        </div>
        <div>
          <SensorRow :stack="stack" />
        </div>
      </section>

      <!-- MLE Section -->
      <section v-if="isEspOnShore" class="compact-section">
        <div class="compact-header-row" style="margin-top: 0.75rem">
          <div class="flex align-items-center">
            <h3 class="compact-title">MLE</h3>
            <CalculatorToggle
              :section-ids="[EquipmentCalculatorSectionKey.MLE]"
            />
          </div>
          <div class="flex align-items-center">
            <LabelledToggle
              v-model="showMLETemperature"
              text="MLE Temperature"
            />
          </div>
        </div>
        <!-- MLE Temperature Chart - Right below header -->
        <transition name="vertical-slide">
          <MLETemperatureChart
            v-if="showMLETemperature"
            enable-mouse-tracking
            :draw-legend="true"
            :is-horizontal="true"
          />
        </transition>
        <MleRow :stack="stack" />
      </section>
    </template>

    <!-- Full Mode Layout (default) -->
    <template v-else>
      <EquipmentHeader class="seal-sensor-mle-header-full">
        <template #title>
          <div class="flex justify-content-between align-items-center">
            {{ !isEspOnShore ? "Thrust Chamber" : "Seals" }}
            <CalculatorToggle :section-ids="sealCalculatorSectionKey" />
          </div>
        </template>
        <template #titleRight>
          <ConverterInput
            class="col-fixed"
            :label="
              isEspOnShore
                ? 'Operating Thrust Load'
                : 'Thrust Load at Duty Point'
            "
            :model-value="operatingThrustLoad"
            :base-unit="MassUnits.Pounds"
            :model="MassUnits.options()"
            mode="convert"
          />
          <ConverterInput
            :key="`${Math.ceil(Math.random() * 10000)}-shut-in-thrust-load`"
            class="col-fixed"
            :label="isEspOnShore ? 'Shut-in Thrust Load' : 'Max Thrust Load'"
            :model-value="shutInThrustLoad"
            :base-unit="MassUnits.Pounds"
            :target-unit="massUnit"
            :model="MassUnits.options()"
            mode="read"
          />
          <ConverterInput
            :key="`${Math.ceil(
              Math.random() * 10000
            )}-selected-seal-thrust-load-rating`"
            class="col-fixed"
            label="Selected Thrust Load Rating"
            :model-value="selectedSealThrustLoadRating"
            :base-unit="MassUnits.Pounds"
            :target-unit="massUnit"
            :model="MassUnits.options()"
            mode="read"
          />
        </template>
      </EquipmentHeader>

      <div>
        <SealMotorOilRow />
      </div>
      <!-- Seals / Thrust Chambers -->
      <div v-if="!isEspOnShore">
        <!-- Show SealThrustChamberRow when isEspOnShore is false -->
        <div v-if="sealsOrder?.length > 0" class="space-y-3">
          <SealThrustChamberRow
            v-for="(sealXrefId, i) in sealsOrder"
            :key="i"
            :index="i"
            :canEdit="!isScenarioReadonly"
            :seal="getSeal(sealXrefId)"
            :hide-add-above="newSealExists || sealsOrder.length >= maxSeals"
            :hide-add-below="newSealExists || sealsOrder.length >= maxSeals"
            :hide-remove="false"
            @add-above="addSeal(i)"
            @add-below="addSeal(i + 1)"
            @remove="removeSeal($event, i)"
            @update:seals="updateSeal($event, i)"
          />
        </div>
        <div v-else class="mb-3">
          <SealThrustChamberRow
            :seal="generateSealXref()"
            :hide-add-above="true"
            :hide-add-below="true"
            :hide-remove="false"
            @update:seals="updateSeal($event, 0)"
          />
        </div>
      </div>
      <div v-else>
        <!-- Show SealRow when isEspOnShore is true -->
        <div v-if="sealsOrder?.length > 0" class="space-y-3">
          <SealRow
            v-for="(sealXrefId, i) in sealsOrder"
            :key="i"
            :index="i"
            :canEdit="!isScenarioReadonly"
            :seal="getSeal(sealXrefId)"
            :stack="stack"
            :hide-add-above="newSealExists"
            :hide-add-below="newSealExists"
            :hide-remove="false"
            @add-above="addSeal(i)"
            @add-below="addSeal(i + 1)"
            @remove="removeSeal($event, i)"
            @update:seals="updateSeal($event, i)"
          />
        </div>
        <div v-else class="mb-3">
          <SealRow
            :seal="generateSealXref()"
            :stack="stack"
            :hide-add-above="true"
            :hide-add-below="true"
            :hide-remove="false"
            @update:seals="updateSeal($event, 0)"
          />
        </div>
      </div>
      <!-- Sensor -->
      <section v-if="isEspOnShore">
        <EquipmentHeader>
          <template #title>Sensor</template>
        </EquipmentHeader>
        <div>
          <SensorRow />
        </div>
      </section>
      <!-- MLE -->
      <section v-if="isEspOnShore">
        <EquipmentHeader>
          <template #title>
            <div class="flex justify-content-between align-items-center">
              MLE
              <CalculatorToggle
                :section-ids="[EquipmentCalculatorSectionKey.MLE]"
              />
            </div>
          </template>
          <template #titleRight>
            <LabelledToggle
              v-model="showMLETemperature"
              text="MLE Temperature"
            />
          </template>
        </EquipmentHeader>
        <transition name="vertical-slide">
          <MLETemperatureChart
            v-if="showMLETemperature"
            enable-mouse-tracking
            :draw-legend="true"
            :is-horizontal="true"
          />
        </transition>
        <MleRow />
      </section>
    </template>
  </EquipmentBase>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import SealRow from "@/components/equipment/seals/SealRow.vue";
import SealThrustChamberRow from "@/components/equipment/seal_thrust_chambers/seals/SealThrustChamberRow.vue";
import EquipmentHeader from "@/components/scenarios/EquipmentHeader.vue";
import EquipmentBase from "@/components/scenarios/EquipmentBase.vue";
import MleRow from "@/components/equipment/mles/MleRow.vue";
import SensorRow from "@/components/equipment/sensors/SensorRow.vue";
import SealMotorOilRow from "@/components/equipment/seal_motor_oil/SealMotorOilRow.vue";
import { v4 } from "uuid";
import CalculatorToggle from "@/components/scenarios/calculator_sections/CalculatorToggle.vue";
import { injectStrict } from "@/utils/utils";
import {
  equipmentFormKey,
  scenarioCalculationsKey,
  scenarioFormKey,
  sealsFormKey,
  scenarioKey,
  sizingKey,
  sizingFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import type { XrefSeals } from "@/composables/forms/formTypes";
import { ScenarioDetailSectionKey } from "@/composables/scenarioCalculations/useEquipmentCalculatorSections";
import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";
import { useEditPermissions } from "@/composables/auth/editPermissions";
import { MassUnits } from "@/libs/units/mass";
import ConverterInput from "@/components/ConverterInput.vue";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import type { ConvertableUnit } from "@/libs/units";
import LabelledToggle from "@/components/LabelledToggle.vue";
import MLETemperatureChart from "@/components/scenarios/charts/MLETemperatureChart.vue";
import { Sizing_Pumping_Type_Enum } from "gql/api";
import type { StackLocation } from "@/components/equipment/d";

const props = defineProps<{
  stack?: StackLocation;
}>();

const sizing = injectStrict(sizingKey, null);
const sizingForm = injectStrict(sizingFormKey, null);
const scenario = injectStrict(scenarioKey);
const scenarioForm = injectStrict(scenarioFormKey, null);
const equipmentForm = injectStrict(equipmentFormKey);
const equipment = computed(() => equipmentForm.data.value);
const sealsForm = injectStrict(sealsFormKey);
const scenarioCalculations = injectStrict(scenarioCalculationsKey);

const { isScenarioReadonly } = useEditPermissions(undefined, scenarioForm);
const { getMassUnit } = useScenarioUnits(scenario, sizing);
const massUnit = computed<ConvertableUnit>(
  () => getMassUnit("Operating Thrust Load.convert.lbs").value
);

const isEspOnShore = computed(() => {
  return (
    sizingForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.EspOnShore
  );
});

const showMLETemperature = ref(false);

const sealsOrder = computed(() => {
  const orderKey =
    props.stack === "lower" ? "lower_seals_order" : "seals_order";
  const order = (equipment.value[orderKey] as string[] | undefined) || [];
  return order.filter((sealId: string) => sealsForm.data.value[sealId]);
});
const maxSeals = computed(() => {
  return scenarioForm?.data.value.pumping_type ===
    Sizing_Pumping_Type_Enum.EspOnShore
    ? 2
    : 1;
});

const newSealExists = computed(() => {
  // Check if there are no seals at all
  if (sealsOrder.value?.length === 0) {
    return true;
  }
  // Check if any seals have null seal_id (indicating incomplete configuration)
  return sealsOrder.value.some((sealId: string) => {
    const seal = sealsForm.data.value[sealId];
    return seal && seal.seal_id === null;
  });
});

const operatingThrustLoad = computed(() =>
  Math.round(
    scenarioCalculations.pumpCalculations.aggregate.operatingThrustLoad.value
  )
);
const shutInThrustLoad = computed(() =>
  Math.round(
    scenarioCalculations.pumpCalculations.aggregate.shutInThrustLoadPounds.value
  )
);

const selectedSealThrustLoadRating = computed(() => {
  const primarySealId = scenarioCalculations.primarySeal.value?.id;
  if (primarySealId) {
    const individualSealCalculations =
      scenarioCalculations.sealCalculations.individual.value;
    return Math.round(
      individualSealCalculations[primarySealId].bearingRatingAtRPMLBS
    );
  }
  return 0; // Default to 0 if primarySealId is undefined or not found in calculations
});

const sealCalculatorSectionKey = computed(() =>
  isEspOnShore.value
    ? [EquipmentCalculatorSectionKey.SEAL]
    : [EquipmentCalculatorSectionKey.THRUST_CHAMBER]
);

function generateSealXref(): XrefSeals[number] {
  const isHps =
    sizingForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.Hps;
  return {
    __typename: "scenario_equipment_xref_seals",
    id: v4(),
    parent_id: null,
    winning_revision_id: null,
    scenario_equipment_id: equipment.value.id,
    seal_id: null,
    winning_revision_parent_id: null,
    winning_revision_created_at: "now()",
    winning_revision_updated_at: "now()",
    winning_revision_deleted_at: null,
    r_seal: null,
    use_high_strength_shaft: isHps
  } as XrefSeals[number];
}

function addSeal(
  index: number,
  xrefSeal?: XrefSeals[number]
): XrefSeals[number] {
  const seal = xrefSeal || generateSealXref();
  const sealIndex = index < 0 ? 0 : index;
  const orderKey =
    props.stack === "lower" ? "lower_seals_order" : "seals_order";
  const newOrder = Object.values(
    (equipment.value[orderKey] as string[] | undefined) || []
  );
  newOrder.splice(sealIndex, 0, seal.id);
  sealsForm.update({
    [seal.id]: seal
  });
  equipmentForm.update({
    [orderKey]: newOrder
  } as Partial<typeof equipmentForm.data.value>);
  return seal;
}

function removeSeal(seal: XrefSeals[number], index: number): void {
  const orderKey =
    props.stack === "lower" ? "lower_seals_order" : "seals_order";
  const newOrder = Object.values(
    (equipment.value[orderKey] as string[] | undefined) || []
  );
  newOrder.splice(index, 1);

  // xref exists in db, soft delete
  if (sealsForm.initialData.value?.[seal.id]) {
    sealsForm.update({
      [seal.id]: {
        ...seal,
        journal_deleted_at: "now()"
      }
    });
  }
  // xref was created but not saved, wipe out
  else {
    delete sealsForm.data.value[seal.id];
  }

  equipmentForm.update({
    [orderKey]: newOrder
  } as Partial<typeof equipmentForm.data.value>);
}

function updateSeal(seal: XrefSeals[number], index: number): void {
  const orderKey =
    props.stack === "lower" ? "lower_seals_order" : "seals_order";
  const newOrder = Object.values(
    (equipment.value[orderKey] as string[] | undefined) || []
  );

  newOrder.splice(index, 1, seal.id);

  sealsForm.update({
    [seal.id]: seal
  });
  equipmentForm.update({
    [orderKey]: newOrder
  } as Partial<typeof equipmentForm.data.value>);
}

function getSeal(id: string): XrefSeals[number] {
  return sealsForm.data.value[id];
}
</script>

<style lang="scss" scoped>
.seal-sensor-mle-header-full {
  :deep(.equipment-header) {
    padding: 0.5rem 0 !important;
    gap: 1rem !important;
    margin-bottom: 0.5rem;
    grid-template-columns: minmax(20px, max-content) 1fr !important;

    .left {
      flex-shrink: 0;
    }

    .right {
      display: flex !important;
      flex: 1 1 auto;
      min-width: 0;
      justify-content: flex-start !important;
      align-items: center;
      gap: 1.5rem !important;
      flex-wrap: wrap;

      > * {
        flex: 0 0 auto;
      }

      .flex {
        gap: 1rem;
      }
    }
  }
}

// Reduce padding for seal/sensor/mle section
:deep(.equipment-row) {
  padding-top: 1em !important;
}
</style>
